import React from 'react'
import { Box, Fade, Typography, Button, Alert } from '@mui/material'
import { AutoAwesome, ArrowForward } from '@mui/icons-material'
import SubscriptionPlans from '../components/SubscriptionPlans'
import SubscriptionAgreementPanel from './SubscriptionAgreement/SubscriptionAgreementPanel'
import { MembershipPlan } from '@/types/subscription'

type Props = {
  showSubscriptionDialog: boolean
  onCloseDialog: () => void
  onOpenDialog: () => void
  onPlanSelect: (
    plan: MembershipPlan,
    cycle: 'monthly' | 'yearly',
    amountPaid: string,
    totalPrice: string,
    couponApplied: string,
    customNoOfUser?: number
  ) => void | Promise<void>
  isProcessingPayment: boolean
  paymentError: string | null
  onClearPaymentError: () => void
  subscriptionPaymentSuccess: boolean
  providersCount?: number
  selectedPlan?: MembershipPlan | null
  billingCycle?: 'monthly' | 'yearly'
  discountedPrice?: number | null
  selectedProviderCount?: number
  agencyName?: string
  agencyId?: string | null
  administratorFirstName?: string
  administratorLastName?: string
  planSelected: boolean
  onActivate: () => void
}

// Note: isProcessingPayment now only drives the "Activate subscription"
// button's loading state (via the activating prop below) — it must not
// unmount SubscriptionAgreementPanel while it's true, or all in-progress
// signing/initials/certification state on that panel is lost.

const ChooseSubscriptionStep: React.FC<Props> = ({
  showSubscriptionDialog,
  onCloseDialog,
  onOpenDialog,
  onPlanSelect,
  isProcessingPayment,
  paymentError,
  onClearPaymentError,
  providersCount,
  selectedPlan,
  billingCycle,
  discountedPrice,
  selectedProviderCount,
  agencyName,
  agencyId,
  administratorFirstName,
  administratorLastName,
  planSelected,
  onActivate,
}) => {
  return (
    <Box>
      <SubscriptionPlans 
        open={showSubscriptionDialog} 
        onClose={onCloseDialog} 
        onPlanSelect={onPlanSelect} 
        providersCount={providersCount}
        agency={true}
        noOfProviders={providersCount}
      />

      {planSelected && (
        <Fade in timeout={800}>
          <Box sx={{ mb: 6 }}>
            {paymentError && (
              <Fade in timeout={800}>
                <Box sx={{ mb: 4 }}>
                  <Alert severity="error" onClose={onClearPaymentError}>
                    {paymentError}
                  </Alert>
                </Box>
              </Fade>
            )}

            <SubscriptionAgreementPanel
              selectedPlan={selectedPlan ?? null}
              billingCycle={billingCycle ?? 'monthly'}
              discountedPrice={discountedPrice ?? null}
              providerCount={selectedProviderCount ?? providersCount ?? 1}
              agencyName={agencyName ?? ''}
              agencyId={agencyId}
              administratorFirstName={administratorFirstName}
              administratorLastName={administratorLastName}
              activating={isProcessingPayment}
              onComplete={onActivate}
            />
          </Box>
        </Fade>
      )}

      {!planSelected && (
        <Fade in timeout={800} style={{ transitionDelay: '400ms' }}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            {paymentError && (
              <Fade in timeout={800}>
                <Box sx={{ mb: 4 }}>
                  <Alert severity="error" onClose={onClearPaymentError}>
                    {paymentError}
                  </Alert>
                </Box>
              </Fade>
            )}

            <Typography
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'grey.800',
                mb: 2,
                fontSize: {
                  xs: '1.75rem',
                  sm: '2rem',
                  md: '2.25rem',
                },
              }}
            >
              Choose Your Subscription Plan
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: 'grey.600',
                maxWidth: '600px',
                mx: 'auto',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                lineHeight: 1.6,
                mb: 4,
              }}
            >
              Choose a plan that best fits your practice needs.
            </Typography>

            {!showSubscriptionDialog ? (
              <Fade in timeout={800} style={{ transitionDelay: '600ms' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={onOpenDialog}
                  startIcon={<AutoAwesome />}
                  endIcon={<ArrowForward />}
                  sx={{
                    bgcolor: '#7A6FA1',
                    color: 'white',
                    px: 4,
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 4px 14px #7A6FA180',
                    '&:hover': {
                      bgcolor: '#7A6FA1',
                      boxShadow: '0 6px 20px #bdb4dfdb',
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  }}
                >
                  Explore Plans & Choose Subscription
                </Button>
              </Fade>
            ) : null}
          </Box>
        </Fade>
      )}
    </Box>
  )
}

export default ChooseSubscriptionStep

